import React from 'react';
import { 
  SparklesIcon, 
  CheckCircleIcon, 
  StarIcon,
  UserGroupIcon,
  TrophyIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const About: React.FC = () => {
  const stats = [
    { icon: <UserGroupIcon className="h-8 w-8 text-accent" />, value: '500+', label: 'Happy Clients' },
    { icon: <TrophyIcon className="h-8 w-8 text-accent" />, value: '1000+', label: 'Projects Completed' },
    { icon: <ClockIcon className="h-8 w-8 text-accent" />, value: '5+', label: 'Years Experience' },
    { icon: <StarIcon className="h-8 w-8 text-accent" />, value: '98%', label: 'Client Satisfaction' }
  ];

  const values = [
    {
      title: 'Quality First',
      description: 'We never compromise on quality. Every project is executed with precision and attention to detail.',
      icon: <CheckCircleIcon className="h-12 w-12 text-accent" />
    },
    {
      title: 'Client Focused',
      description: 'Your success is our success. We work closely with you to understand and exceed your expectations.',
      icon: <UserGroupIcon className="h-12 w-12 text-accent" />
    },
    {
      title: 'Innovation',
      description: 'We stay ahead of trends and use the latest technology to deliver cutting-edge solutions.',
      icon: <SparklesIcon className="h-12 w-12 text-accent" />
    }
  ];

  const team = [
    {
      name: 'Creative Director',
      role: 'Design & Strategy',
      description: 'Leading our creative vision and ensuring every project meets the highest standards.'
    },
    {
      name: 'Production Manager',
      role: 'Operations & Quality',
      description: 'Overseeing production processes and maintaining our commitment to excellence.'
    },
    {
      name: 'Client Relations',
      role: 'Customer Success',
      description: 'Building lasting relationships and ensuring client satisfaction at every step.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-dark text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              About Electus Creative
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
              We are a proudly Zimbabwean company specializing in creative marketing, printing, branding, and corporate identity solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Our mission is to help brands communicate effectively through design, print, and product innovation. 
                We believe that every business deserves to stand out and make a lasting impression.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                From small startups to large corporations, we provide comprehensive creative solutions that 
                drive growth and build strong brand identities.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-accent mr-3" />
                  <span className="text-gray-700">Professional design and branding services</span>
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-accent mr-3" />
                  <span className="text-gray-700">High-quality printing and production</span>
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-accent mr-3" />
                  <span className="text-gray-700">Custom corporate solutions</span>
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-accent mr-3" />
                  <span className="text-gray-700">Exceptional customer service</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-primary mb-4">Why Choose Us?</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="bg-accent/10 p-2 rounded-lg mr-4">
                    <SparklesIcon className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary mb-1">Creative Excellence</h4>
                    <p className="text-gray-600 text-sm">Innovative designs that capture your brand essence</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-accent/10 p-2 rounded-lg mr-4">
                    <CheckCircleIcon className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary mb-1">Quality Assurance</h4>
                    <p className="text-gray-600 text-sm">Rigorous quality control on every project</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-accent/10 p-2 rounded-lg mr-4">
                    <UserGroupIcon className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary mb-1">Client Partnership</h4>
                    <p className="text-gray-600 text-sm">Long-term relationships built on trust</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Our Impact
            </h2>
            <p className="text-xl text-gray-600">
              Numbers that speak to our commitment to excellence.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-4">
                  {stat.icon}
                </div>
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="flex justify-center mb-6">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold text-primary mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Our Team
            </h2>
            <p className="text-xl text-gray-600">
              Meet the dedicated professionals behind Electus Creative.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-20 h-20 bg-accent rounded-full mx-auto mb-6 flex items-center justify-center">
                  <UserGroupIcon className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-primary mb-2">
                  {member.name}
                </h3>
                <p className="text-accent font-medium mb-4">
                  {member.role}
                </p>
                <p className="text-gray-600">
                  {member.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Let's Work Together
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Ready to elevate your brand? Contact us today to discuss your project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="btn-accent text-lg px-8 py-3"
            >
              Get in Touch
            </a>
            <a
              href="/services"
              className="btn-outline border-white text-white hover:bg-white hover:text-primary text-lg px-8 py-3"
            >
              View Services
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
